import { Button, Col, Modal, Row, Select, Typography } from 'antd'
import { useState, useEffect, useCallback } from 'react'
import { Batch } from '@a4b/api/src/modules/Monetisation/Puja/types'
import ReactPlayer from 'react-player'
interface Props {
    visible: boolean
    closeModal: () => void
    handleDoneQc: () => void
    videoUrl: string
    batchData: Batch | undefined
    okTitle: string
    closeTitle: string
    title: string
    isPujaService: boolean
}
const CompletedPreviewModal: React.FC<Props> = ({
    visible,
    handleDoneQc,
    closeModal,
    videoUrl,
    batchData,
    okTitle,
    closeTitle,
    title,
    isPujaService
}) => {
    // State for multi-temple/multi-day video selection
    const [selectedTempleId, setSelectedTempleId] = useState<string | null>(null)
    const [selectedDay, setSelectedDay] = useState<number | null>(null)
    const [currentVideoUrl, setCurrentVideoUrl] = useState<string>(videoUrl)

    // Helper function to check if we should show dropdowns
    const shouldShowDropdowns = (batch: any) => {
        return batch?.multi_temple || batch?.multi_day
    }

    // Helper function to get video URL for specific temple/day combination
    const getVideoUrlForSelection = useCallback((batch: any, templeId: string | null, day: number | null) => {
        if (!batch?.temples_data || !templeId || day === null) {
            return batch?.video_url || videoUrl || ''
        }

        const temple = batch.temples_data.find((t: any) => t.temple_id === templeId)
        if (!temple) return batch?.video_url || videoUrl || ''

        const schedule = temple?.schedules?.find((s: any) => s.day === day)
        if (!schedule) return batch?.video_url || videoUrl || ''

        return schedule?.video_url || batch?.video_url || videoUrl || ''
    }, [videoUrl])

    // Initialize with first temple and day when modal opens
    useEffect(() => {
        if (visible && batchData && shouldShowDropdowns(batchData)) {
            const firstTemple = batchData?.temples_data?.[0]
            const firstDay = firstTemple?.schedules?.[0]?.day ?? null

            setSelectedTempleId(firstTemple?.temple_id || null)
            setSelectedDay(firstDay)

            const initialUrl = getVideoUrlForSelection(batchData, firstTemple?.temple_id || null, firstDay)
            setCurrentVideoUrl(initialUrl)
        } else if (visible) {
            // For single temple/day, use the provided videoUrl
            setCurrentVideoUrl(videoUrl)
        }
    }, [visible, batchData, videoUrl, getVideoUrlForSelection])

    // Handle temple change
    const handleTempleChange = (templeId: string) => {
        setSelectedTempleId(templeId)

        const temple = batchData?.temples_data?.find((t: any) => t.temple_id === templeId)
        const firstDay = temple?.schedules?.[0]?.day ?? null

        setSelectedDay(firstDay)

        const newUrl = getVideoUrlForSelection(batchData, templeId || null, firstDay)
        setCurrentVideoUrl(newUrl)
    }

    // Handle day change
    const handleDayChange = (day: number) => {
        setSelectedDay(day)

        const newUrl = getVideoUrlForSelection(batchData, selectedTempleId, day)
        setCurrentVideoUrl(newUrl)
    }

    const footer = []
    const youtubeUrl: string = 'https://www.youtube.com/'

    if (closeTitle) {
        footer.push(<Button key='cancel' onClick={closeModal}>
            {closeTitle}
        </Button>)
    }
    if (okTitle) {
        footer.push(<Button key='done' type='primary' onClick={handleDoneQc}>
            {okTitle}
        </Button>)
    }
    console.log(batchData, "wertyu")
    return (
        <Modal
            title={title}
            centered
            closable={true}
            visible={visible}
            onCancel={closeModal}
            width={shouldShowDropdowns(batchData) ? 600 : 400}
            destroyOnClose={true}
            footer={footer}
        >
            {/* Show temple and day dropdowns for multi-temple or multi-day batches */}
            {shouldShowDropdowns(batchData) && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '6px' }}>
                    <Typography.Text strong style={{ display: 'block', marginBottom: '12px' }}>
                        Select Temple & Day to Preview Video:
                    </Typography.Text>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                        {batchData?.multi_temple && (
                            <div style={{ flex: 1 }}>
                                <Typography.Text style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                    Temple:
                                </Typography.Text>
                                <Select
                                    placeholder="Select temple"
                                    onChange={handleTempleChange}
                                    value={selectedTempleId}
                                    style={{ width: '100%' }}
                                    size="small"
                                >
                                    {batchData?.temples_data?.map((temple: any) => (
                                        <Select.Option key={temple.temple_id} value={temple.temple_id}>
                                            {temple.temple_name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        )}

                        {batchData?.multi_day && (
                            <div style={{ flex: 1 }}>
                                <Typography.Text style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                    Day:
                                </Typography.Text>
                                <Select
                                    placeholder="Select day"
                                    onChange={handleDayChange}
                                    value={selectedDay}
                                    style={{ width: '100%' }}
                                    size="small"
                                    disabled={!selectedTempleId}
                                >
                                    {selectedTempleId &&
                                        batchData?.temples_data
                                            ?.find((t: any) => t.temple_id === selectedTempleId)
                                            ?.schedules?.map((schedule: any) => (
                                                <Select.Option key={schedule.day} value={schedule.day}>
                                                    {`Day ${schedule.day} (${schedule.process_date || 'N/A'})`}
                                                </Select.Option>
                                            ))}
                                </Select>
                            </div>
                        )}
                    </div>

                    <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
                        Currently viewing: {selectedTempleId && selectedDay ?
                            `${batchData?.temples_data?.find((t: any) => t.temple_id === selectedTempleId)?.temple_name} - Day ${selectedDay}` :
                            'Please select temple and day'
                        }
                    </Typography.Text>
                </div>
            )}

            <Row justify='center'>
                <Col span={24} style={{ justifySelf: "center" }}>
                    {currentVideoUrl ? (
                        currentVideoUrl.match(youtubeUrl) ? (
                            <ReactPlayer
                                url={currentVideoUrl}
                                width='100%'
                                height='100%'
                                controls={true}
                            />
                        ) : (
                            <video width='100%' height='400' controls>
                                <source src={currentVideoUrl} type='video/mp4'></source>
                                Your browser does not support the video tag.
                            </video>
                        )
                    ) : (
                        <div style={{
                            height: '300px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f0f0f0',
                            borderRadius: '6px',
                            border: '1px dashed #d9d9d9'
                        }}>
                            <Typography.Text type="secondary">
                                {shouldShowDropdowns(batchData) ?
                                    'No video available for selected temple/day' :
                                    'No video URL available'
                                }
                            </Typography.Text>
                        </div>
                    )}
                </Col>
            </Row>
        </Modal>
    )
}

export default CompletedPreviewModal
